import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, Globe, MapPin, Package, Users, DollarSign, TrendingUp,
  Calendar, ChefHat, Wine, Music, Camera, Sparkles, Truck,
  Loader2, CheckCircle2, ExternalLink, BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  food: { icon: ChefHat, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  drinks: { icon: Wine, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  entertainment: { icon: Music, color: 'text-pink-400', bg: 'bg-pink-500/10' },
  staffing: { icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  photography: { icon: Camera, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  decor: { icon: Sparkles, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  logistics: { icon: Truck, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
};

function StatCard({ label, value, icon: Icon, color = 'text-indigo-400', sub }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2.5 rounded-xl ${color.replace('text-', 'bg-').replace('400', '500/10')}`}>
          <Icon className={`h-5 w-5 ${color}`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
          {sub && <p className="text-[10px] text-muted-foreground/70">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function MarketExpansionDashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/admin/market-expansion`, { headers });
      setData(res.data);
    } catch { toast.error('Failed to load market data'); }
    finally { setLoading(false); }
  };

  if (user?.role !== 'admin') return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const { markets = [], summary = {} } = data || {};
  const detail = selectedMarket ? markets.find(m => m.city_key === selectedMarket) : null;

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="back-to-admin">
              <ArrowLeft className="h-4 w-4 mr-1" /> Admin
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
                <Globe className="h-6 w-6 text-indigo-500" /> Market Expansion Dashboard
              </h1>
              <p className="text-sm text-muted-foreground">Service coverage, pricing, and performance across all markets</p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate('/admin/products')} data-testid="manage-products-btn">
            <Package className="h-4 w-4 mr-1" /> Manage Products
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8" data-testid="summary-stats">
          <StatCard label="Active Markets" value={summary.total_markets} icon={Globe} color="text-indigo-400" />
          <StatCard label="Total Services" value={summary.total_services} icon={Package} color="text-emerald-400" />
          <StatCard label="Providers" value={summary.total_providers} icon={Users} color="text-blue-400" />
          <StatCard label="Proposals" value={summary.total_proposals} icon={BarChart3} color="text-amber-400" />
          <StatCard label="Revenue" value={`$${(summary.total_revenue || 0).toLocaleString()}`} icon={DollarSign} color="text-green-400" />
        </div>

        {/* Market Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8" data-testid="market-cards">
          {markets.map(market => (
            <Card
              key={market.city_key}
              className={`bg-card border-border cursor-pointer transition-all hover:border-indigo-500/40 ${selectedMarket === market.city_key ? 'ring-2 ring-indigo-500 border-indigo-500/50' : ''}`}
              onClick={() => setSelectedMarket(selectedMarket === market.city_key ? null : market.city_key)}
              data-testid={`market-${market.city_key}`}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-lg font-bold text-foreground">{market.label}</h3>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {market.zip} &middot; {market.radius_miles}mi radius
                    </p>
                  </div>
                  <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Live</Badge>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="text-center p-2 bg-muted/30 rounded-lg">
                    <p className="text-xl font-bold text-foreground">{market.services_active}</p>
                    <p className="text-[10px] text-muted-foreground">Services</p>
                  </div>
                  <div className="text-center p-2 bg-muted/30 rounded-lg">
                    <p className="text-xl font-bold text-foreground">{market.providers_count}</p>
                    <p className="text-[10px] text-muted-foreground">Providers</p>
                  </div>
                  <div className="text-center p-2 bg-muted/30 rounded-lg">
                    <p className="text-xl font-bold text-foreground">${market.avg_price}</p>
                    <p className="text-[10px] text-muted-foreground">Avg Price</p>
                  </div>
                </div>

                {/* Category Bars */}
                <div className="space-y-1.5">
                  {Object.entries(market.categories).sort((a, b) => b[1].count - a[1].count).map(([cat, info]) => {
                    const cfg = CATEGORY_CONFIG[cat] || { icon: Package, color: 'text-gray-400', bg: 'bg-gray-500/10' };
                    const Icon = cfg.icon;
                    const pct = Math.round((info.count / market.services_active) * 100);
                    return (
                      <div key={cat} className="flex items-center gap-2 text-xs">
                        <Icon className={`h-3 w-3 ${cfg.color} flex-shrink-0`} />
                        <span className="w-20 capitalize text-muted-foreground truncate">{cat}</span>
                        <div className="flex-1 h-2 bg-muted/40 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${cfg.bg.replace('/10', '/60')}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-5 text-right text-muted-foreground">{info.count}</span>
                      </div>
                    );
                  })}
                </div>

                <p className="text-[10px] text-muted-foreground mt-3">
                  Launched {new Date(market.launched).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {market.tax_rate && <span className="ml-2">Tax: {market.tax_rate}%</span>}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Detailed View */}
        {detail && (
          <Card className="bg-card border-border" data-testid="market-detail">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-indigo-400" />
                {detail.label} — Detailed Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Providers */}
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-2">Providers</h4>
                <div className="space-y-2">
                  {detail.providers.map((prov, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg border border-border">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center">
                          <Users className="h-4 w-4 text-indigo-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{prov.name}</p>
                          <p className="text-[10px] text-muted-foreground">{prov.services} services</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {prov.gcal && (
                          <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">
                            <CheckCircle2 className="h-3 w-3 mr-0.5" /> GCal
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Category Pricing Table */}
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-2">Category Pricing</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-3 text-muted-foreground font-medium">Category</th>
                        <th className="text-center py-2 px-3 text-muted-foreground font-medium">Services</th>
                        <th className="text-right py-2 px-3 text-muted-foreground font-medium">Min Price</th>
                        <th className="text-right py-2 px-3 text-muted-foreground font-medium">Avg Price</th>
                        <th className="text-right py-2 px-3 text-muted-foreground font-medium">Max Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(detail.categories).sort((a, b) => b[1].count - a[1].count).map(([cat, info]) => {
                        const cfg = CATEGORY_CONFIG[cat] || { icon: Package, color: 'text-gray-400' };
                        const Icon = cfg.icon;
                        return (
                          <tr key={cat} className="border-b border-border/50 hover:bg-muted/10">
                            <td className="py-2 px-3 capitalize flex items-center gap-2">
                              <Icon className={`h-3.5 w-3.5 ${cfg.color}`} /> {cat}
                            </td>
                            <td className="py-2 px-3 text-center">{info.count}</td>
                            <td className="py-2 px-3 text-right text-muted-foreground">${info.min_price}</td>
                            <td className="py-2 px-3 text-right font-semibold">${info.avg_price}</td>
                            <td className="py-2 px-3 text-right text-muted-foreground">${info.max_price}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Performance */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="text-center p-3 bg-indigo-500/5 border border-indigo-500/20 rounded-lg">
                  <p className="text-xl font-bold text-foreground">{detail.proposals_count}</p>
                  <p className="text-[10px] text-muted-foreground">Proposals</p>
                </div>
                <div className="text-center p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                  <p className="text-xl font-bold text-emerald-400">${detail.revenue.toLocaleString()}</p>
                  <p className="text-[10px] text-muted-foreground">Revenue</p>
                </div>
                <div className="text-center p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <p className="text-xl font-bold text-foreground">{Object.keys(detail.categories).length}</p>
                  <p className="text-[10px] text-muted-foreground">Categories</p>
                </div>
                <div className="text-center p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                  <p className="text-xl font-bold text-foreground">{detail.radius_miles}mi</p>
                  <p className="text-[10px] text-muted-foreground">Service Radius</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Market Comparison Table */}
        {!selectedMarket && markets.length > 1 && (
          <Card className="bg-card border-border" data-testid="market-comparison">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-indigo-400" /> Market Comparison
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Market</th>
                      <th className="text-center py-2 px-3 text-muted-foreground font-medium">Services</th>
                      <th className="text-center py-2 px-3 text-muted-foreground font-medium">Providers</th>
                      <th className="text-center py-2 px-3 text-muted-foreground font-medium">Categories</th>
                      <th className="text-right py-2 px-3 text-muted-foreground font-medium">Avg Price</th>
                      <th className="text-right py-2 px-3 text-muted-foreground font-medium">Proposals</th>
                      <th className="text-right py-2 px-3 text-muted-foreground font-medium">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {markets.map(m => (
                      <tr key={m.city_key} className="border-b border-border/50 hover:bg-muted/10 cursor-pointer" onClick={() => setSelectedMarket(m.city_key)}>
                        <td className="py-2.5 px-3 font-medium flex items-center gap-2">
                          <MapPin className="h-3.5 w-3.5 text-indigo-400" /> {m.label}
                        </td>
                        <td className="py-2.5 px-3 text-center">{m.services_active}</td>
                        <td className="py-2.5 px-3 text-center">{m.providers_count}</td>
                        <td className="py-2.5 px-3 text-center">{Object.keys(m.categories).length}</td>
                        <td className="py-2.5 px-3 text-right font-semibold">${m.avg_price}</td>
                        <td className="py-2.5 px-3 text-right">{m.proposals_count}</td>
                        <td className="py-2.5 px-3 text-right text-emerald-400">${m.revenue.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
